/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    ICategoryHierarchyViewProps,
    IRefineMenuViewProps,
    ISearchResultContainerViewProps,
    ISearchResultModalViewProps,
    ISortByViewProps,
    ITitleViewProps,
    ISearchResultContainerData
} from '@msdyn365-commerce-modules/search-result-container';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import { getProductPageUrlSync } from '@msdyn365-commerce-modules/retail-actions';
import * as React from 'react';
import { getByIdAsync, getByIdsAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { parseString } from 'xml2js';
import { ObjectExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { LogLevel } from '@msdyn365-commerce/retail-proxy/dist/interfaces/ITelemetry';
import { IBuyBoxConfigurator } from './components/buyboxconfigurator';
import { getCartState } from '@msdyn365-commerce/global-state';
import { ProductSearchResult, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { logDetails } from '../../../utils/debug-mode';
import { ISearchResultContainerProps } from '../definition-extensions/search-result-container.ext.props.autogenerated';

export interface IFullProductsSearchResultsWithCount {
    products: ProductSearchResult[];
    count: number;
    channelInventoryConfigurationId?: number;
    inventoryAwareSortableAttributeId?: number;
}

const SearchResultContainerView: React.FC<ISearchResultContainerViewProps &
    ISearchResultContainerProps<ISearchResultContainerData>> = props => {
    const {
        SearchResultContainer,
        pagination,
        ProductsContainer,
        ProductSectionContainer,
        choiceSummary,
        isMobile,
        modalToggle,
        searchResultModal,
        TitleViewProps,
        refineMenu,
        categoryHierarchy,
        sortByOptions,
        CategoryNavContainer,
        RefineAndProductSectionContainer,
        errorMessage,
        FeatureSearchContainer,
        similarLookProduct,
        products,
        config
    } = props;
    const { resources } = props;
    logDetails(props.context.app.config.debugMode, props);
    const isRecoSearchPage = props.context.actionContext.requestContext.query?.recommendation;

    const [productss, setProducts] = React.useState<SimpleProduct[]>([]);
    const [cartError, setCartError] = React.useState<string>('');
    const [productIds, setProductIds] = React.useState<number[]>([]);

    // @ts-ignore
    const allProducts: any = products.props;

    const getPrice = (price: number | undefined) => {
        if (typeof price !== 'undefined') {
            return props.context.cultureFormatter.formatCurrency(price);
        } else {
            return '';
        }
    };

    const context = props.context.actionContext;

    const getProductPageUrl = (name: string | undefined, recordId: string | number | undefined) => {
        return getProductPageUrlSync(name as string, recordId as number, context);
    };

    const addToCart = async (product: any) => {
        const cartState = await getCartState(props.context.actionContext);

        let cartResult = await cartState.addProductToCart({
            product: product
        });

        if (cartResult.status !== 'SUCCESS') {
            setCartError((cartResult.validationResults && cartResult.validationResults[0].ErrorContext) || '');
        }
    };

    const iContext = {
        callerContext: props.context.actionContext
    };

    const apiSettings = props.context.request.apiSettings;

    const checkConfigurableProduct = (recordId: number) => {
        getByIdAsync(iContext, recordId, apiSettings.channelId)
            .then(product => {
                if (
                    !ObjectExtensions.isNullOrUndefined(product) &&
                    !ObjectExtensions.isNullOrUndefined(product.ExtensionProperties) &&
                    !ObjectExtensions.isNullOrUndefined(product.ExtensionProperties.find(prop => prop.Key === 'INSCONFIGURATION'))
                ) {
                    const insConfiguration = product.ExtensionProperties.find(prop => prop.Key === 'INSCONFIGURATION')?.Value?.StringValue;

                    if (!ObjectExtensions.isNullOrUndefined(insConfiguration) && insConfiguration !== '') {
                        parseString(insConfiguration, (err: Error | null, result: IBuyBoxConfigurator) => {
                            if (err) {
                                props.context.telemetry.log(LogLevel.Error, err.message);
                                return;
                            }
                            window.location.href = getProductPageUrl(product.Name, product.RecordId);
                        });
                    } else {
                        addToCart(product);
                    }
                }
            })
            .catch(err => {
                logDetails(props.context.app.config.debugMode, err);
            });
    };

    React.useEffect(() => {
        if (allProducts && allProducts.products && allProducts.products.length > 0) {
            const recordIds = allProducts.products.map((product: any) => product.RecordId);
            setProductIds(recordIds);
        }
    }, []);

    React.useEffect(() => {
        if (productIds.length > 0) {
            getByIdsAsync(iContext, apiSettings.channelId, productIds).then(res => {
                setProducts(res);
            });
        }
    }, [productIds, allProducts]);

    if (isMobile) {
        return (
            <Module {...SearchResultContainer}>
                {categoryHierarchy && renderCategoryHierarchy(categoryHierarchy)}
                {renderTitleBar(TitleViewProps)}
                {renderTitle(TitleViewProps)}
                {choiceSummary}
                {modalToggle}
                {createSearchResultModal(searchResultModal, refineMenu, sortByOptions, isRecoSearchPage)}
                <Node {...FeatureSearchContainer}>{similarLookProduct}</Node>
                <Node {...ProductsContainer}>
                    {errorMessage}
                    {cartError && <div className='ms-search-result__error'>{cartError}</div>}
                    {/* {products} */}
                    <div className='msc-search-result-container-list'>
                        <ul className='list-unstyled'>
                            {allProducts &&
                                allProducts.products &&
                                allProducts.products.length > 0 &&
                                allProducts.products.map((product: any) => {
                                    return (
                                        <li key={product.ItemId} className='ms-product-search-result__item'>
                                            <a
                                                href={getProductPageUrl(product.Name, product.RecordId)}
                                                className='msc-product'
                                                data-exp-event-id={`Category.Search result container.${product.RecordId}.click`}
                                            >
                                                <div className='msc-product__image'>
                                                    <picture>
                                                        <img
                                                            alt={product.Name}
                                                            src={`${product.PrimaryImageUrl}`}
                                                            data-src={`${product.PrimaryImageUrl}`}
                                                            className='msc-main_image msc_image lazyloaded'
                                                        />
                                                    </picture>
                                                </div>
                                                <h4 className='msc-product__title'>{product.Name}</h4>
                                            </a>
                                            <div className='msc-product__reviews'>
                                                <div className='msc-rating msc-no-ratings ms-rating-component__up'>
                                                    <div aria-hidden='true' className='msc-rating__count'>
                                                        {/* Reviews ({product.TotalRatings}) */}
                                                        {resources.reviews} ({product.TotalRatings})
                                                    </div>
                                                    <div className='msc-rating__range sr-only'></div>
                                                    <div aria-hidden='true' className='msc-rating__group'>
                                                        {product.AverageRating
                                                            ? [1, 2, 3, 4, 5].map(item => {
                                                                  return (
                                                                      <span
                                                                          key={item}
                                                                          className={` ${
                                                                              item > (product.AverageRating ? product.AverageRating : 0)
                                                                                  ? 'msc-rating__empty-star'
                                                                                  : 'msc-rating__star'
                                                                          }`}
                                                                          data-index={item}
                                                                          role='presentation'
                                                                      ></span>
                                                                  );
                                                              })
                                                            : [1, 2, 3, 4, 5].map(item => {
                                                                  return (
                                                                      <span
                                                                          key={item}
                                                                          className={`msc-rating__empty-star`}
                                                                          data-index={item}
                                                                          role='presentation'
                                                                      ></span>
                                                                  );
                                                              })}
                                                    </div>
                                                </div>
                                                <div className='msc-product__details'>
                                                    <span className='msc-price'>
                                                        <p className='msc-price__actual' itemProp='price'>
                                                            {getPrice(product.Price)}
                                                        </p>
                                                        <p style={{ fontSize: 'small', textAlign: 'right' }}>
                                                            {config.excludeVatLabel
                                                                ? config.excludeVatLabel.length > 0
                                                                    ? `(${config.excludeVatLabel})`
                                                                    : ''
                                                                : ''}
                                                        </p>
                                                    </span>
                                                </div>
                                            </div>
                                            <div className={`ml-product__buttons`} data-label={product.RecordId}>
                                                {productss.find(val => val.RecordId === product.RecordId)?.ExtensionProperties &&
                                                productss
                                                    .find(val => val.RecordId === product.RecordId)
                                                    ?.ExtensionProperties?.find(prop => prop.Key === 'INSSAMPLEITEMID')?.Value
                                                    ?.StringValue ? (
                                                    <a
                                                        href={
                                                            productss
                                                                .find(val => val.RecordId === product.RecordId)
                                                                ?.ExtensionProperties?.find(prop => prop.Key === 'INSSAMPLEITEMID')?.Value
                                                                ?.StringValue
                                                        }
                                                        className='ml-product__sample-button'
                                                    >
                                                        {/* Order Sample */}
                                                        {resources.orderSample}
                                                    </a>
                                                ) : (
                                                    <></>
                                                )}
                                                <button
                                                    style={
                                                        productss.find(val => val.RecordId === product.RecordId)?.ExtensionProperties &&
                                                        productss
                                                            .find(val => val.RecordId === product.RecordId)
                                                            ?.ExtensionProperties?.find(prop => prop.Key === 'INSSAMPLEITEMID')?.Value
                                                            ?.StringValue
                                                            ? { width: '45%' }
                                                            : { width: '100%' }
                                                    }
                                                    onClick={() => checkConfigurableProduct(product.RecordId)}
                                                    className='ml-product__cart-button'
                                                >
                                                    {resources.addToBasket}
                                                </button>
                                            </div>
                                            <div className='msc-product__questions'>
                                                <a href={getProductPageUrl(product.Name, product.RecordId) + '#reviews'} target='_blank'>
                                                    {resources.askQuesion}
                                                </a>
                                            </div>
                                        </li>
                                    );
                                })}
                        </ul>
                    </div>
                </Node>
                {pagination}
            </Module>
        );
    }

    return (
        <Module {...SearchResultContainer}>
            {categoryHierarchy && <Node {...CategoryNavContainer}>{renderCategoryHierarchy(categoryHierarchy)}</Node>}
            <Node {...RefineAndProductSectionContainer}>
                {refineMenu &&
                    renderRefiner({
                        ...refineMenu,
                        categorySourceValue: props.config.categorySourceValue
                    })}

                <Node {...ProductSectionContainer}>
                    <Node {...FeatureSearchContainer}>{similarLookProduct}</Node>
                    <div className='ml-search-result-wrapper-category-title'>{renderTitleBar(TitleViewProps)}</div>
                    <div className='ms-search-result__count-sort'>
                        <div className='ms-search-result-wrapper-title-choice-summary'>{TitleViewProps && renderTitle(TitleViewProps)}</div>
                        <div className='ms-search-result-wrapper-sort-by-category'>
                            {sortByOptions && !isRecoSearchPage && renderSort(sortByOptions)}
                        </div>
                    </div>
                    <div className='ms-search-result-summary'>{choiceSummary}</div>
                    <div className='ms-search-result'></div>
                    <Node {...ProductsContainer}>
                        {errorMessage}
                        {cartError && <div className='ms-search-result__error'>{cartError}</div>}
                        {/* {products} */}
                        <div className='msc-search-result-container-list'>
                            <ul className='list-unstyled'>
                                {allProducts &&
                                    allProducts.products &&
                                    allProducts.products.length > 0 &&
                                    allProducts.products.map((product: any) => {
                                        return (
                                            <li key={product.ItemId} className='ms-product-search-result__item'>
                                                <a
                                                    href={getProductPageUrl(product.Name, product.RecordId)}
                                                    className='msc-product'
                                                    data-exp-event-id={`Category.Search result container.${product.RecordId}.click`}
                                                >
                                                    <div className='msc-product__image'>
                                                        <picture>
                                                            <img
                                                                alt={product.Name}
                                                                src={`${product.PrimaryImageUrl}`}
                                                                data-src={`${product.PrimaryImageUrl}`}
                                                                className='msc-main_image msc_image lazyloaded'
                                                            />
                                                        </picture>
                                                    </div>
                                                    <h4 className='msc-product__title'>{product.Name}</h4>
                                                </a>
                                                <div className='msc-product__reviews'>
                                                    <div className='msc-rating msc-no-ratings ms-rating-component__up'>
                                                        <output aria-hidden='true' className='msc-rating__count'>
                                                            {/* Reviews ({product.TotalRatings}) */}
                                                            {resources.reviews} ({product.TotalRatings})
                                                            </output>
                                                        <div className='msc-rating__range sr-only'></div>
                                                        <div aria-hidden='true' className='msc-rating__group'>
                                                            {product.AverageRating
                                                                ? [1, 2, 3, 4, 5].map(item => {
                                                                      return (
                                                                          <span
                                                                              key={item}
                                                                              className={` ${
                                                                                  item > (product.AverageRating ? product.AverageRating : 0)
                                                                                      ? 'msc-rating__empty-star'
                                                                                      : 'msc-rating__star'
                                                                              }`}
                                                                              data-index={item}
                                                                              role='presentation'
                                                                          ></span>
                                                                      );
                                                                  })
                                                                : [1, 2, 3, 4, 5].map(item => {
                                                                      return (
                                                                          <span
                                                                              key={item}
                                                                              className={`msc-rating__empty-star`}
                                                                              data-index={item}
                                                                              role='presentation'
                                                                          ></span>
                                                                      );
                                                                  })}
                                                        </div>
                                                    </div>
                                                    <div className='msc-product__details'>
                                                        <span className='msc-price'>
                                                            <p className='msc-price__actual' itemProp='price'>
                                                                {getPrice(product.Price)}
                                                            </p>
                                                            <p style={{ fontSize: 'small', textAlign: 'right' }}>
                                                                {config.excludeVatLabel
                                                                    ? config.excludeVatLabel.length > 0
                                                                        ? `(${config.excludeVatLabel})`
                                                                        : ''
                                                                    : ''}
                                                            </p>
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className={`ml-product__buttons`} data-label={product.RecordId}>
                                                    {productss.find(val => val.RecordId === product.RecordId)?.ExtensionProperties &&
                                                    productss
                                                        .find(val => val.RecordId === product.RecordId)
                                                        ?.ExtensionProperties?.find(prop => prop.Key === 'INSSAMPLEITEMID')?.Value
                                                        ?.StringValue ? (
                                                        <a
                                                            href={
                                                                productss
                                                                    .find(val => val.RecordId === product.RecordId)
                                                                    ?.ExtensionProperties?.find(prop => prop.Key === 'INSSAMPLEITEMID')
                                                                    ?.Value?.StringValue
                                                            }
                                                            className='ml-product__sample-button'
                                                        >
                                                            {/* Order Sample */}
                                                            {resources.orderSample}
                                                        </a>
                                                    ) : (
                                                        <></>
                                                    )}
                                                    <button
                                                        style={
                                                            productss.find(val => val.RecordId === product.RecordId)?.ExtensionProperties &&
                                                            productss
                                                                .find(val => val.RecordId === product.RecordId)
                                                                ?.ExtensionProperties?.find(prop => prop.Key === 'INSSAMPLEITEMID')?.Value
                                                                ?.StringValue
                                                                ? { width: '45%' }
                                                                : { width: '100%' }
                                                        }
                                                        onClick={() => checkConfigurableProduct(product.RecordId)}
                                                        className='ml-product__cart-button'
                                                    >
                                                        {resources.addToBasket}
                                                    </button>
                                                </div>
                                                <div className='msc-product__questions'>
                                                    <a
                                                        href={getProductPageUrl(product.Name, product.RecordId) + '#reviews'}
                                                        target='_blank'
                                                    >
                                                        {resources.askQuesion}
                                                    </a>
                                                </div>
                                            </li>
                                        );
                                    })}
                            </ul>
                        </div>
                    </Node>
                    {pagination}
                </Node>
            </Node>
        </Module>
    );
};

const createSearchResultModal = (
    modalProps: ISearchResultModalViewProps,
    refineMenu: IRefineMenuViewProps,
    sortByDropDown: ISortByViewProps,
    isRecoSearchPage?: string
): JSX.Element => {
    return React.cloneElement(
        modalProps.modal,
        {},
        modalProps.modalHeader,
        createModalBody(modalProps, refineMenu, sortByDropDown, isRecoSearchPage),
        modalProps.modalFooter
    );
};

const createModalBody = (
    props: ISearchResultModalViewProps,
    refineMenu: IRefineMenuViewProps,
    sortByDropDown: ISortByViewProps,
    isRecoSearchPage?: string
): JSX.Element | null => {
    if (sortByDropDown) {
        return React.cloneElement(props.modalBody, {}, renderSort(sortByDropDown, isRecoSearchPage), renderRefiner(refineMenu));
    }
    return null;
};

const renderRefiner = (
    props: IRefineMenuViewProps & {
        categorySourceValue?: number;
    }
): JSX.Element | null => {
    const { refiners, RefineMenuContainer, RefinerSectionContainer, categorySourceValue } = props;

    if (refiners) {
        return (
            <Node {...RefinerSectionContainer}>
                <Node {...RefineMenuContainer}>
                    <h2 className='ml-search-container__filter-label'>Shopping Options</h2>
                    {refiners.map((submenu, index) => {
                        // To hide category refiner
                        // @ts-ignore
                        if (submenu.props.productRefinerHierarchy.SourceValue === categorySourceValue) {
                            return <></>;
                        }

                        return <React.Fragment key={index}>{submenu}</React.Fragment>;
                    })}
                </Node>
            </Node>
        );
    }
    return null;
};

const renderSort = (props: ISortByViewProps, isRecoSearchPage?: string): JSX.Element | null => {
    const { SortingContainer, sortByDropDown } = props;
    if (sortByDropDown && !isRecoSearchPage) {
        return <Node {...SortingContainer}>{sortByDropDown}</Node>;
    }
    return null;
};

const renderCategoryHierarchy = (props: ICategoryHierarchyViewProps): JSX.Element | null => {
    const { CategoryHierarchyContainer, categoryHierarchyList, categoryHierarchySeparator } = props;
    if (categoryHierarchyList) {
        return (
            <Node {...CategoryHierarchyContainer}>
                {categoryHierarchyList.map((category, index) => (
                    <React.Fragment key={index}>
                        {category}
                        {categoryHierarchyList && categoryHierarchyList[index + 1] && categoryHierarchySeparator}
                    </React.Fragment>
                ))}
            </Node>
        );
    }

    return null;
};

const renderTitleBar = (props: ITitleViewProps): JSX.Element | null => {
    const { title, TitleContainer } = props;
    if (title) {
        return (
            <Node {...TitleContainer}>
                {/* <h2>
                    {title.titlePrefix}
                    {title.titleText}({title.titleCount})
                </h2> */}
                <div className='ml-product__title-bar'>
                    <h2>{title.titleText}</h2>
                </div>
            </Node>
        );
    }
    return null;
};
const renderTitle = (props: ITitleViewProps): JSX.Element | null => {
    const { title, TitleContainer } = props;
    if (title) {
        return (
            <Node {...TitleContainer}>
                <h2>{title.titleCount}</h2>
            </Node>
        );
    }
    return null;
};

export default SearchResultContainerView;
